import { ChangeEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { Eye, FileUp } from "lucide-react";
import {
  createAdminDraft,
  DraftUploadFilePayload,
  fetchAdminProject,
  ProjectDetail,
  ProjectStatus,
  sendAdminDraft,
  updateAdminProject,
  WorkflowApiError,
} from "../services/workflow";
import {
  formatCurrency,
  formatDateTime,
  getStripePaymentStatusLabel,
  getProjectStatusMeta,
} from "../services/workflow-ui";
import { useAuth } from "../contexts/AuthContext";

const projectStatusOptions: Array<{ value: ProjectStatus; label: string }> = [
  { value: "questionnaire_received", label: "Neue Anfrage" },
  { value: "draft_in_progress", label: "Entwurf in Arbeit" },
  { value: "draft_ready", label: "Entwurf bereit" },
  { value: "draft_sent", label: "Beim Kunden" },
  { value: "revision_requested", label: "Revision offen" },
  { value: "ordered", label: "Bestellt" },
  { value: "assets_pending", label: "Wartet auf Daten" },
  { value: "finalizing", label: "Finalisierung" },
  { value: "live", label: "Live" },
];

function getApiOrigin() {
  const baseUrl = import.meta.env.VITE_API_BASE_URL || "/api";
  if (/^https?:\/\//i.test(baseUrl)) {
    const parsed = new URL(baseUrl);
    return parsed.origin;
  }

  return window.location.origin;
}

function readDraftFileAsBase64(file: File) {
  return new Promise<DraftUploadFilePayload>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Datei konnte nicht gelesen werden"));
        return;
      }

      const [, base64Data = ""] = result.split(",", 2);
      const relativePath = file.webkitRelativePath
        ? file.webkitRelativePath.split("/").slice(1).join("/")
        : file.name;

      resolve({
        fileName: file.name,
        relativePath: relativePath || file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        base64Data,
      });
    };

    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.readAsDataURL(file);
  });
}

const directoryInputProps = {
  webkitdirectory: "",
  directory: "",
} as Record<string, string>;

export function AdminProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { impersonateCustomer } = useAuth();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [projectForm, setProjectForm] = useState({
    status: "questionnaire_received" as ProjectStatus,
    internalNotes: "",
    liveUrl: "",
    finalNotes: "",
  });
  const [selectedDraftFiles, setSelectedDraftFiles] = useState<File[]>([]);
  const [draftUploadKey, setDraftUploadKey] = useState(0);
  const [draftPersonalMessage, setDraftPersonalMessage] = useState("");
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isSendingDraft, setIsSendingDraft] = useState(false);
  const [isOpeningCustomerView, setIsOpeningCustomerView] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [draftSendInfo, setDraftSendInfo] = useState<{
    previewPassword: string;
    recipientEmail: string;
  } | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadProject = async () => {
      if (!projectId) {
        setError("Projekt-ID fehlt");
        setIsLoading(false);
        return;
      }

      try {
        const nextProject = await fetchAdminProject(projectId);

        if (!isMounted) {
          return;
        }

        setProject(nextProject);
        setProjectForm({
          status: nextProject.status,
          internalNotes: nextProject.internalNotes,
          liveUrl: nextProject.liveUrl,
          finalNotes: nextProject.finalNotes,
        });
        setDraftPersonalMessage(nextProject.currentDraft?.personalMessage || "");
      } catch (loadError) {
        if (isMounted) {
          setError(
            loadError instanceof WorkflowApiError
              ? loadError.message
              : "Projekt konnte nicht geladen werden",
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProject();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Projekt wird geladen...</div>;
  }

  if (error || !project) {
    return (
      <div className="space-y-4">
        <Link to="/admin/projects" className="text-sm text-gray-600 hover:text-black">
          Zurueck zu den Projekten
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error || "Projekt wurde nicht gefunden"}
        </div>
      </div>
    );
  }

  const statusMeta = getProjectStatusMeta(project.status);
  const publicPreviewUrl = `${window.location.origin}/preview/${
    project.currentDraft?.shareSlug || ""
  }`;
  const uploadsOrigin = getApiOrigin();

  const applyProject = (nextProject: ProjectDetail, message: string) => {
    setProject(nextProject);
    setProjectForm({
      status: nextProject.status,
      internalNotes: nextProject.internalNotes,
      liveUrl: nextProject.liveUrl,
      finalNotes: nextProject.finalNotes,
    });
    setSelectedDraftFiles([]);
    setDraftUploadKey((current) => current + 1);
    setDraftPersonalMessage(nextProject.currentDraft?.personalMessage || "");
    setError("");
    setSuccessMessage(message);
  };

  const handleProjectSave = async () => {
    setSuccessMessage("");
    setDraftSendInfo(null);
    setIsSavingProject(true);

    try {
      const nextProject = await updateAdminProject(project.id, projectForm);
      applyProject(nextProject, "Projektstatus aktualisiert");
    } catch (saveError) {
      setError(
        saveError instanceof WorkflowApiError
          ? saveError.message
          : "Projekt konnte nicht gespeichert werden",
      );
    } finally {
      setIsSavingProject(false);
    }
  };

  const handleCreateDraft = async () => {
    if (selectedDraftFiles.length === 0) {
      setError("Bitte laden Sie den dist-Ordner hoch");
      return;
    }

    setSuccessMessage("");
    setError("");
    setDraftSendInfo(null);
    setIsSavingDraft(true);

    try {
      const files = await Promise.all(selectedDraftFiles.map((file) => readDraftFileAsBase64(file)));
      const response = await createAdminDraft(project.id, { files });
      applyProject(response.project, "Neue Entwurfsversion automatisch erstellt");
    } catch (saveError) {
      setError(
        saveError instanceof WorkflowApiError
          ? saveError.message
          : "Entwurf konnte nicht angelegt werden",
      );
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDraftFilesChange = (event: ChangeEvent<HTMLInputElement>) => {
    setError("");
    setSelectedDraftFiles(Array.from(event.target.files || []));
  };

  const handleSendDraft = async () => {
    if (!project.currentDraft) {
      return;
    }

    setSuccessMessage("");
    setError("");
    setIsSendingDraft(true);

    try {
      const response = await sendAdminDraft(project.currentDraft.id, {
        personalMessage: draftPersonalMessage,
      });
      applyProject(response.project, "Entwurfsmail wurde erzeugt und versendet");
      setDraftSendInfo({
        previewPassword: response.project.currentDraft?.previewPassword || "",
        recipientEmail: response.email.recipientEmail,
      });
    } catch (sendError) {
      setError(
        sendError instanceof WorkflowApiError
          ? sendError.message
          : "Entwurfsmail konnte nicht versendet werden",
      );
    } finally {
      setIsSendingDraft(false);
    }
  };

  const handleOpenCustomerView = async () => {
    if (!project) {
      return;
    }

    setIsOpeningCustomerView(true);
    setSuccessMessage("");
    setError("");

    try {
      await impersonateCustomer(project.customer.userId);
      navigate("/dashboard");
    } catch (impersonationError) {
      setError(
        impersonationError instanceof WorkflowApiError
          ? impersonationError.message
          : impersonationError instanceof Error && impersonationError.message
            ? impersonationError.message
            : "Kundenansicht konnte nicht geöffnet werden",
      );
    } finally {
      setIsOpeningCustomerView(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <Link to="/admin/projects" className="text-sm text-gray-600 hover:text-black">
            Zurueck zu den Projekten
          </Link>
          <h1 className="mt-2 text-3xl font-bold text-gray-900">{project.title}</h1>
          <p className="mt-2 text-gray-600">
            {project.customer.company || project.customer.name} · {project.customer.email}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              void handleOpenCustomerView();
            }}
            disabled={isOpeningCustomerView}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-900 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Eye size={16} />
            {isOpeningCustomerView ? "Oeffnet..." : "Kundenansicht"}
          </button>
          <span
            className={`w-fit rounded-full px-3 py-1 text-sm font-medium ${statusMeta.className}`}
          >
            {statusMeta.label}
          </span>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {successMessage}
        </div>
      ) : null}

      {project.paymentIssue ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {project.paymentIssue.message}
          {project.paymentIssue.paymentStatus ? (
            <span className="mt-1 block font-semibold">
              Stripe-Status: {getStripePaymentStatusLabel(project.paymentIssue.paymentStatus)}
            </span>
          ) : null}
        </div>
      ) : null}

      {project.order && !project.order.isPaymentConfirmed ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Auftrag wurde bereits übernommen, aber der tatsächliche Zahlungseingang ist noch nicht
          final bestätigt.
          {project.order.paymentStatus ? (
            <span className="mt-1 block font-semibold">
              Stripe-Status: {getStripePaymentStatusLabel(project.order.paymentStatus)}
            </span>
          ) : null}
        </div>
      ) : null}

      {draftSendInfo ? (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          <p>Entwurfsmail an {draftSendInfo.recipientEmail} vorbereitet.</p>
          <p className="mt-2">
            Entwurfs-Passwort für die Vorschau:
            <span className="ml-2 rounded bg-white px-2 py-1 font-mono text-blue-900">
              {draftSendInfo.previewPassword}
            </span>
          </p>
          <p className="mt-2 text-blue-900">
            Ein Login-Passwort wird beim Entwurfsversand nicht mehr erzeugt. Die Passwortvergabe
            erfolgt erst nach erfolgreicher Bestellung.
          </p>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-500">Ansprechpartner</p>
          <p className="mt-2 text-lg font-semibold text-gray-900">
            {project.questionnaire.contactName}
          </p>
          <p className="text-sm text-gray-600">{project.questionnaire.contactEmail}</p>
          <p className="text-sm text-gray-600">{project.questionnaire.contactPhone || "-"}</p>
          <p className="mt-3 text-sm text-gray-500">
            Fragebogen: {formatDateTime(project.questionnaire.createdAt)}
          </p>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-500">Paket</p>
          <p className="mt-2 text-lg font-semibold text-gray-900">
            {formatCurrency(project.pricing.setup)} + {formatCurrency(project.pricing.monthly)} / Monat
          </p>
          <p className="mt-3 text-sm text-gray-600">
            Revisionen: {project.revisions.used}/{project.revisions.included}
          </p>
          <p className="text-sm text-gray-600">Uploads: {project.assetCount}</p>
          <p className="text-sm text-gray-600">
            Bestellung: {project.order ? formatDateTime(project.order.acceptedAt) : "Noch offen"}
          </p>
          {project.paymentIssue ? (
            <p className="text-sm text-red-700">
              Zahlung: Problem bei der Bestätigung
            </p>
          ) : null}
          {project.order ? (
            <p className="text-sm text-gray-600">
              Zahlung: {getStripePaymentStatusLabel(project.order.paymentStatus)}
            </p>
          ) : null}
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <p className="text-sm text-gray-500">Aktueller Entwurf</p>
          <p className="mt-2 text-lg font-semibold text-gray-900">
            {project.currentDraft ? project.currentDraft.title : "Noch kein Entwurf"}
          </p>
          <p className="mt-2 text-sm text-gray-600">
            {project.currentDraft ? `Version ${project.currentDraft.version}` : "Bitte Entwurf anlegen."}
          </p>
          {project.currentDraft ? (
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={publicPreviewUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Entwurf oeffnen
              </a>
              <a
                href={publicPreviewUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50"
              >
                Kundenlink
              </a>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Projektstatus pflegen</h2>
            <p className="text-sm text-gray-600">Interne Notizen, Live-URL und finaler Status.</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-2 text-sm text-gray-700">
              <span>Status</span>
              <select
                value={projectForm.status}
                onChange={(event) =>
                  setProjectForm((current) => ({
                    ...current,
                    status: event.target.value as ProjectStatus,
                  }))
                }
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:border-black focus:outline-none"
              >
                {projectStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-2 text-sm text-gray-700">
              <span>Live-URL</span>
              <input
                type="url"
                value={projectForm.liveUrl}
                onChange={(event) =>
                  setProjectForm((current) => ({
                    ...current,
                    liveUrl: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:border-black focus:outline-none"
                placeholder="https://..."
              />
            </label>
          </div>

          <label className="space-y-2 text-sm text-gray-700">
            <span>Interne Notizen</span>
            <textarea
              value={projectForm.internalNotes}
              onChange={(event) =>
                setProjectForm((current) => ({
                  ...current,
                  internalNotes: event.target.value,
                }))
              }
              rows={5}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </label>

          <label className="space-y-2 text-sm text-gray-700">
            <span>Abschlussnotizen</span>
            <textarea
              value={projectForm.finalNotes}
              onChange={(event) =>
                setProjectForm((current) => ({
                  ...current,
                  finalNotes: event.target.value,
                }))
              }
              rows={4}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
            />
          </label>

          <button
            type="button"
            onClick={handleProjectSave}
            disabled={isSavingProject}
            className="rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSavingProject ? "Speichert..." : "Projekt speichern"}
          </button>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Entwurf verwalten</h2>
            <p className="text-sm text-gray-600">
              Dist-Ordner hochladen, Vorschau automatisch erzeugen und danach an den Kunden
              senden.
            </p>
          </div>

          <label className="space-y-2 text-sm text-gray-700">
            <span>Astro dist-Ordner</span>
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4">
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <FileUp size={18} />
                <span>Bitte den gebauten `dist`-Ordner auswaehlen.</span>
              </div>
              <input
                key={draftUploadKey}
                type="file"
                multiple
                onChange={handleDraftFilesChange}
                className="mt-4 block w-full text-sm text-gray-700 file:mr-4 file:rounded-lg file:border-0 file:bg-black file:px-4 file:py-2 file:font-medium file:text-white hover:file:bg-gray-800"
                {...directoryInputProps}
              />
              <p className="mt-3 text-xs text-gray-500">
                {selectedDraftFiles.length > 0
                  ? `${selectedDraftFiles.length} Dateien für den nächsten Entwurf ausgewählt`
                  : "Noch kein dist-Ordner ausgewählt"}
              </p>
            </div>
          </label>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={handleCreateDraft}
              disabled={isSavingDraft || selectedDraftFiles.length === 0}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSavingDraft ? "Erstellt..." : "Neue Version aus dist erstellen"}
            </button>
          </div>

          <label className="space-y-2 text-sm text-gray-700">
            <span>Persönliche Nachricht für die Entwurfsmail (optional)</span>
            <textarea
              value={draftPersonalMessage}
              onChange={(event) => setDraftPersonalMessage(event.target.value)}
              rows={5}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-black focus:outline-none"
              placeholder="Zum Beispiel: Wir haben besonders auf die mobile Darstellung und die Startseite geachtet."
            />
            <p className="text-xs text-gray-500">
              Wird in der E-Mail eingeblendet und spaeter im Header des Entwurfs per Hover
              angezeigt.
            </p>
          </label>

          <button
            type="button"
            onClick={handleSendDraft}
            disabled={!project.currentDraft || isSendingDraft}
            className="rounded-lg bg-black px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSendingDraft ? "Versendet..." : "Entwurf an Kunden senden"}
          </button>

          {project.currentDraft ? (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
              Oeffentlicher Link:{" "}
              <a
                href={publicPreviewUrl}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-black underline"
              >
                {publicPreviewUrl}
              </a>
            </div>
          ) : null}
        </section>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-gray-900">Fragebogen</h2>
          <p className="mt-1 text-sm text-gray-600">
            Rohdaten der eingegangenen Anfrage für die Entwurfserstellung.
          </p>
          <pre className="mt-4 overflow-x-auto rounded-xl bg-gray-950 p-4 text-sm leading-relaxed text-gray-100">
            {JSON.stringify(project.questionnaire.submissionPayload, null, 2)}
          </pre>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-gray-900">Revisionen</h2>
          <div className="mt-4 space-y-3">
            {project.revisionsList.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                Bisher wurden keine Revisionen eingereicht.
              </p>
            ) : null}

            {project.revisionsList.map((revision) => (
              <div key={revision.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-gray-900">Revision {revision.roundNumber}</p>
                  <span className="text-xs text-gray-500">{formatDateTime(revision.createdAt)}</span>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                  {revision.feedback}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-gray-900">Uploads & Zugangsdaten</h2>
          <div className="mt-4 space-y-3">
            {project.assets.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                Noch keine Daten oder Dateien vom Kunden vorhanden.
              </p>
            ) : null}

            {project.assets.map((asset) => (
              <div key={asset.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{asset.label}</p>
                    <p className="text-xs text-gray-500">{formatDateTime(asset.createdAt)}</p>
                  </div>

                  {asset.downloadUrl ? (
                    <a
                      href={`${uploadsOrigin}${asset.downloadUrl}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-medium text-black underline"
                    >
                      Datei oeffnen
                    </a>
                  ) : null}
                </div>

                {asset.contentText ? (
                  <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">
                    {asset.contentText}
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6">
          <h2 className="text-xl font-semibold text-gray-900">Mail-Log</h2>
          <div className="mt-4 space-y-3">
            {project.emails.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-sm text-gray-500">
                Es wurden noch keine Mails erzeugt.
              </p>
            ) : null}

            {project.emails.map((email) => (
              <div key={email.id} className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-gray-900">{email.subject}</p>
                    <p className="text-sm text-gray-600">{email.recipientEmail}</p>
                  </div>
                  <span className="text-xs text-gray-500">{formatDateTime(email.createdAt)}</span>
                </div>
                <pre className="mt-3 overflow-x-auto whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-xs leading-relaxed text-gray-700">
                  {email.bodyText}
                </pre>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
